import { FC, useEffect, useRef, type ReactNode } from 'react';
import type { GiftCardItem, NewCardData } from '../../domain/models';
import { translate } from '../../i18n';
import { cardRowSubtitle } from '../../analytics/labels';
import { type HostViewport } from '../../browser/embedSource';
import { isDesktop } from '../../platform';
import { PAYMENT_IFRAME_MESSAGE_SOURCE } from '../../browser/reportFrameHeight';
import { useMinWidth, WEB_DESKTOP_MIN_WIDTH } from '../../browser/useMinWidth';
import { AddCardForm } from '../AddCardForm';
import { AmountInput } from '../AmountInput';
import { CVVInput } from '../CVVInput';
import { GiftCardPromo } from '../GiftCardPromo';
import { GiftCardPromoModal } from '../GiftCardPromoModal';
import { InstallmentSelector, type InstallmentPlan } from '../InstallmentSelector';
import { PaymentErrorBox } from '../PaymentErrorBox';
import { PaymentFailurePanel } from '../PaymentFailurePanel';
import { PaymentMethodCarousel, paymentMethodItemId, type PaymentMethodItem } from '../PaymentMethodCarousel';
// import { AddressInfoBox } from '../AddressInfoBox';
import addCardIcon from '../../assets/icons/addCard.svg';
import addCardIconAlt from '../../assets/icons/addCardAlt.svg';
import addCardBlueIcon from '../../assets/icons/addCardBlue.svg';
import plusAlt from '../../assets/icons/plusAlt.svg';
import { SplitPaymentCardSection, SplitSectionDivider } from '../SplitPaymentCardSection';
import { TogglePaymentCardsButton } from '../TogglePaymentCardsButton';
import { PaymentMethodsAddButtonSkeleton, PaymentMethodsSkeleton } from './PaymentMethodsSkeleton';
import { webPaymentStyles as styles } from './styles';
import carrefourMark from '../../assets/icons/paymentCarrefourSvg.svg';
import carrefourDigitalMark from '../../assets/icons/paymentCarrefourDigitalSvg.svg';
import visaMark from '../../assets/icons/paymentVisaSvg.svg';
import mastercardMark from '../../assets/icons/paymentMastercardSvg.svg';
import amexMark from '../../assets/icons/paymentAmexSvg.svg';
import mercadoPagoMark from '../../assets/icons/paymentMercadoPagoSvg.svg';
import trashIcon from '../../assets/icons/thrashSvg.svg';
import mercadoPagoFlow from '../../assets/mercado-pago-panel/logo.png';
import mpExternalIcon from '../../assets/mercado-pago-panel/external-link.png';
import mpUserIcon from '../../assets/mercado-pago-panel/user.png';
import mpCardIcon from '../../assets/mercado-pago-panel/card.png';
import mpAlertIcon from '../../assets/mercado-pago-panel/alert.png';

const MERCADO_PAGO_NOTES = [
  { key: 'paymentsUI.web.mercadoPago.login', icon: mpUserIcon },
  { key: 'paymentsUI.web.mercadoPago.methods', icon: mpCardIcon },
  { key: 'paymentsUI.web.mercadoPago.refund', icon: mpAlertIcon },
] as const;

/** El texto entre ** ** se muestra en negrita, como en el checkout de la tienda. */
const withBold = (text: string) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, index) =>
    part.startsWith('**') ? <strong key={index}>{part.slice(2, -2)}</strong> : part,
  );

/** Errores de cuotas se muestran junto al selector; no duplicar el banner global. */
const showGlobalPaymentError = (props: WebPaymentProps): boolean =>
  !!props.error && !props.installmentsErrorMessage;

export interface WebPaymentProps {
  /** Mientras se resuelve `GET …/payment/card`, no mostrar “Agregar tarjeta” ni Mercado Pago. */
  loadingMethods?: boolean;
  /** `false` hasta el primer listado de métodos exitoso (no mostrar MP/“agregar” tras un error temprano). */
  paymentMethodsReady?: boolean;
  paymentMethods: PaymentMethodItem[];
  installmentPlans: InstallmentPlan[];
  secondInstallmentPlans?: InstallmentPlan[];
  total: number;
  selectedMethodId?: string;
  cvv?: string;
  selectedInstallmentId?: string;
  splitPaymentEnabled: boolean;
  firstCardId?: string;
  secondCardId?: string;
  firstAmount?: number;
  secondAmount?: number;
  secondCvv?: string;
  secondInstallmentId?: string;
  loading: boolean;
  error?: string;
  canSubmit: boolean;
  installmentsLoading?: boolean;
  installmentsErrorMessage?: string;
  onRetryInstallments?: () => void;
  address: string;
  giftCardModalOpen: boolean;
  onSelectMethod: (id: string) => void;
  onCvvChange: (cvv: string) => void;
  onCvvError?: (error: string | undefined) => void;
  onSelectInstallment: (id: string) => void;
  onToggleSplit: () => void;
  onShowAddCard: () => void;
  onSelectFirstCard: (item: PaymentMethodItem) => void;
  onSelectSecondCard: (item: PaymentMethodItem) => void;
  onFirstAmountChange: (value: number | undefined) => void;
  onSecondAmountChange: (value: number | undefined) => void;
  onSecondCvvChange: (cvv: string) => void;
  onSelectSecondInstallment: (id: string) => void;
  onSubmit: () => void;
  onOpenGiftCard: () => void;
  onCloseGiftCard: () => void;
  giftCards: GiftCardItem[];
  onApplyGiftCard: (redemptionCode: string) => Promise<void>;
  onRemoveGiftCard: (giftCardId: string) => Promise<void>;
  addingCard: boolean;
  onRemoveCard: (id: string) => void;
  /** Con datos: tokeniza y guarda la tarjeta nueva (rechaza si falla). Sin datos: cancela el alta. */
  onSaveCard: (card?: NewCardData) => Promise<void>;
  /** Viewport del checkout que embebe. `null` si el iframe no está en la web. */
  hostViewport?: HostViewport | null;
  /** Tras un `initiate` fallido: pantalla Figma en lugar del listado de métodos. */
  paymentFailureDetail?: string;
  onDismissPaymentFailure?: () => void;
}

function rowTitle(item: PaymentMethodItem): string {
  if (item.kind === 'card') {
    return translate('paymentsUI.web.cardRow', {
      issuer: item.issuerName,
      last4: item.last4,
    });
  }
  if (item.kind === 'mercadopago') return translate('paymentsUI.web.mercadoPagoRow');
  return translate('paymentsUI.addPaymentCard.title');
}

const awaitingPaymentMethods = (props: Pick<WebPaymentProps, 'loadingMethods' | 'paymentMethodsReady'>) =>
  props.loadingMethods === true || props.paymentMethodsReady === false;

function rowSubtitle(item: PaymentMethodItem): string | undefined {
  if (item.kind !== 'card') return undefined;
  return cardRowSubtitle(item);
}

function brandSrc(network: string, issuer: string): string | null {
  const name = issuer.toLowerCase();
  if (name.includes('carrefour')) {
    return name.includes('digital') ? carrefourDigitalMark : carrefourMark;
  }
  const net = network.toLowerCase();
  if (net.includes('visa')) return visaMark;
  if (net.includes('master')) return mastercardMark;
  if (net.includes('amex') || net.includes('american')) return amexMark;
  return null;
}

const BrandMark: FC<{ network: string; issuer: string }> = ({ network, issuer }) => {
  const src = brandSrc(network, issuer);
  if (!src) return null;
  return <img src={src} alt="" style={styles.brandIcon} />;
};

export const WebPayment: FC<WebPaymentProps> = (props) => {
  const isWide = useMinWidth(WEB_DESKTOP_MIN_WIDTH);
  // Embebido, el ancho del frame es el de la columna. El host manda el viewport de la página.
  const isDesktopList = isDesktop ? props.hostViewport !== 'mobile' : isWide;

  return (
    <div
      style={{
        ...styles.page,
        ...(props.addingCard && !isDesktopList
          ? {
              height: '100%',
              minHeight: '100%',
            }
          : null),
      }}
      data-embed-source={isDesktop ? 'desktop' : 'app'}
      data-web-layout={isDesktopList ? 'list' : 'mobile'}
      data-testid={isDesktopList ? 'web-payment-desktop' : 'web-payment-mobile'}
    >
      {isDesktopList ? <DesktopList {...props} /> : <MobileWeb {...props} />}
      {!isDesktop && (
        <GiftCardPromoModal
          isOpen={props.giftCardModalOpen}
          onClose={props.onCloseGiftCard}
          giftCards={props.giftCards}
          onApply={props.onApplyGiftCard}
          onRemove={props.onRemoveGiftCard}
          testID="web-gift-card-modal"
        />
      )}
    </div>
  );
};

const DesktopList: FC<WebPaymentProps> = (props) => {
  const cards = props.paymentMethods.filter((item) => item.kind === 'card');
  const wasSplit = useRef(props.splitPaymentEnabled);

  useEffect(() => {
    if (props.splitPaymentEnabled && !wasSplit.current) {
      const active = document.activeElement;
      if (active instanceof HTMLElement) active.blur();
      const snap = () => {
        window.scrollTo(0, 0);
        try {
          window.parent.scrollTo(0, 0);
        } catch {
          /* El iframe publicado es cross-origin: el host escucha scroll-top. */
        }
        window.parent.postMessage(
          { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'scroll-top' },
          '*',
        );
      };
      snap();
      requestAnimationFrame(() => {
        snap();
        requestAnimationFrame(snap);
      });
    }
    wasSplit.current = props.splitPaymentEnabled;
  }, [props.splitPaymentEnabled]);

  return (
    <section style={styles.desktopMain}>
      {!isDesktop && (
        <div style={styles.stepTitleRow}>
          <span style={styles.stepDot} />
          <h1 style={styles.stepTitle}>{translate('paymentsUI.web.stepTitle')}</h1>
        </div>
      )}
      <PaymentColumn {...props} cards={cards} />
    </section>
  );
};

const MobileWeb: FC<WebPaymentProps> = (props) => {
  const cards = props.paymentMethods.filter((item) => item.kind === 'card');
  const selectedIsCard = props.selectedMethodId?.startsWith('card-') ?? false;

  useEffect(() => {
    if (props.hostViewport !== 'mobile' || !props.addingCard) return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const previous = {
      html: html.style.height,
      body: body.style.height,
      root: root?.style.height ?? '',
    };
    html.style.height = '100%';
    body.style.height = '100%';
    if (root) root.style.height = '100%';
    return () => {
      html.style.height = previous.html;
      body.style.height = previous.body;
      if (root) root.style.height = previous.root;
    };
  }, [props.addingCard, props.hostViewport]);

  useEffect(() => {
    if (props.hostViewport !== 'mobile') return;
    window.parent.postMessage(
      { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'add-card', open: props.addingCard },
      '*',
    );
    if (props.addingCard) {
      window.parent.postMessage(
        { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'scroll-top' },
        '*',
      );
    }
  }, [props.addingCard, props.hostViewport]);

  useEffect(() => {
    if (props.hostViewport !== 'mobile') return;
    return () => {
      window.parent.postMessage(
        { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'add-card', open: false },
        '*',
      );
    };
  }, [props.hostViewport]);

  return (
    <div style={props.addingCard ? styles.sheetStage : undefined}>
      <GiftCardPromo
        onPress={props.onOpenGiftCard}
        testID="web-gift-card-promo"
        prompText={translate('paymentsUI.web.giftBanner')}
      />
      <div style={styles.mobileMain}>
        <h2 style={styles.mobileMethodsTitle}>{translate('paymentsUI.web.chooseHowToPay')}</h2>
        {props.paymentFailureDetail && props.onDismissPaymentFailure ? (
          <PaymentFailurePanel
            variant="paymentDeclined"
            detail={props.paymentFailureDetail}
            onAction={props.onDismissPaymentFailure}
            testID="web-payment-failure-mobile"
          />
        ) : null}
        {!props.paymentFailureDetail && !props.splitPaymentEnabled && (
          <div style={styles.mobileMethodsHeader}>
            <p style={styles.mobileMethodsSubtitle}>{translate('paymentsUI.web.methodsTitle')}</p>
            {awaitingPaymentMethods(props) ? (
              <PaymentMethodsAddButtonSkeleton variant="mobile" />
            ) : (
              <button type="button" onClick={props.onShowAddCard} style={styles.mobileAdd} data-testid="web-mobile-add-card">
                <img src={addCardBlueIcon} alt="" style={styles.addCardBlueIcon} />
                {translate('paymentsUI.web.add')}
              </button>
            )}
          </div>
        )}

        {!props.paymentFailureDetail && !props.splitPaymentEnabled && awaitingPaymentMethods(props) && (
          <PaymentMethodsSkeleton
            variant="carousel-mobile"
            carouselInset="web-mobile"
            withSelectedCardFlow
            testID="web-payment-methods-loading"
          />
        )}
        {!props.paymentFailureDetail && !props.splitPaymentEnabled && !awaitingPaymentMethods(props) && (
          <PaymentMethodCarousel
            items={props.paymentMethods}
            selectedId={props.selectedMethodId}
            onSelect={(item) => props.onSelectMethod(paymentMethodItemId(item))}
            onAddPress={props.onShowAddCard}
            onRemove={(item) => item.kind === 'card' && props.onRemoveCard(item.id)}
            testID="web-payment-carousel"
            contained
          />
        )}

        {!props.paymentFailureDetail && selectedIsCard && !props.splitPaymentEnabled && (
          <div style={styles.mobileBlock}>
            <CVVInput
              value={props.cvv}
              onChange={props.onCvvChange}
              onError={props.onCvvError}
              testID="web-cvv-input"
            />
            <TogglePaymentCardsButton mode="add" onPress={props.onToggleSplit} testID="web-toggle-cards" />
            <InstallmentSelector
              plans={props.installmentPlans}
              selectedId={props.selectedInstallmentId || ''}
              onSelect={props.onSelectInstallment}
              title={translate('paymentsUI.web.installmentsTitle')}
              testID="web-installments"
              loading={props.installmentsLoading}
              errorMessage={props.installmentsErrorMessage}
              onRetry={props.onRetryInstallments}
            />
          </div>
        )}

        {!props.paymentFailureDetail && props.splitPaymentEnabled && <SplitColumns {...props} cards={cards} />}

        {/* {!props.paymentFailureDetail && props.selectedMethodId && (
          <div style={styles.mobileBlock}>
            <AddressInfoBox
              address={props.address}
              title={translate('paymentsUI.addressInfo.cardBillingAddressTitle')}
              testID="web-address-info"
            />
          </div>
        )} */}

        {!props.paymentFailureDetail && showGlobalPaymentError(props) && (
          <div style={styles.mobileBlock}>
            <PaymentErrorBox message={props.error!} testID="web-payment-error" />
          </div>
        )}

      </div>
      {props.addingCard && !props.paymentFailureDetail && (
        <>
          <div style={styles.sheetBackdrop} aria-hidden="true" />
          <div className="web-add-card-sheet" style={styles.sheet} data-testid="web-mobile-add-sheet">
            <AddCardForm
              variant="sheet"
              onBack={() => void props.onSaveCard()}
              onSave={props.onSaveCard}
              testID="web-add-card"
            />
          </div>
        </>
      )}
    </div>
  );
};

const PaymentColumn: FC<WebPaymentProps & { cards: PaymentMethodItem[] }> = (props) => (
  <>
    {props.paymentFailureDetail && props.onDismissPaymentFailure ? (
      <PaymentFailurePanel
        variant="paymentDeclined"
        detail={props.paymentFailureDetail}
        onAction={props.onDismissPaymentFailure}
        testID="web-payment-failure"
      />
    ) : props.addingCard && !props.splitPaymentEnabled ? (
      <AddCardForm
        variant="inline"
        onBack={() => void props.onSaveCard()}
        onSave={props.onSaveCard}
        testID="web-add-card"
      />
    ) : (
      <div style={styles.methodsCard}>
        <div style={{ ...styles.listHeader, ...(props.hostViewport === 'mobile' ? styles.listHeaderMobile : null) }}>
          <p style={styles.chooseLabel}>{translate('paymentsUI.web.chooseHowToPay')}</p>
          {!props.splitPaymentEnabled &&
            (awaitingPaymentMethods(props) ? (
              <PaymentMethodsAddButtonSkeleton variant="desktop" />
            ) : (
              <button type="button" style={styles.addCardButton} onClick={props.onShowAddCard}>
                <img src={addCardIconAlt} alt="" style={styles.addCardIcon} />
                {translate('paymentsUI.web.addCard')}
              </button>
            ))}
        </div>
        {props.splitPaymentEnabled ? (
          <DesktopSplit {...props} />
        ) : awaitingPaymentMethods(props) ? (
          <PaymentMethodsSkeleton testID="web-payment-methods-loading" />
        ) : (
          <MethodList {...props} />
        )}
      </div>
    )}
    {showGlobalPaymentError(props) && (
      <div style={styles.block}>
        <PaymentErrorBox message={props.error!} testID="web-payment-error" />
      </div>
    )}
  </>
);

const MethodList: FC<WebPaymentProps> = (props) => {
  const cards = props.paymentMethods.filter((item) => item.kind === 'card');
  const mercadoPago = props.paymentMethods.find((item) => item.kind === 'mercadopago');
  const mercadoPagoId = mercadoPago && mercadoPago.kind === 'mercadopago' ? mercadoPago.id : 'mercadopago';

  return (
    <ul style={styles.list} role="radiogroup" aria-label={translate('paymentsUI.web.chooseHowToPay')}>
      {cards.length === 0 && (
        <li>
          <button type="button" style={styles.dashedAdd} onClick={props.onShowAddCard}>
            <img src={plusAlt} alt="" style={styles.plusIcon} />
            {translate('paymentsUI.web.addCardEmpty')}
          </button>
        </li>
      )}
      {cards.map((item) => {
        if (item.kind !== 'card') return null;
        const selected = props.selectedMethodId === item.id;
        return (
          <li key={item.id}>
            <div style={{ ...styles.row, ...(selected ? styles.rowSelected : null) }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  style={{ ...styles.rowButton, flex: 1, width: 'auto' }}
                  onClick={() => props.onSelectMethod(item.id)}
                >
                  {selected && <span style={{ ...styles.radio, ...styles.radioOn }} />}
                  <BrandMark network={item.network} issuer={item.issuerName} />
                  <span style={styles.rowText}>
                    <span style={styles.rowTitle}>{rowTitle(item)}</span>
                    <span style={styles.rowSubtitle}>{rowSubtitle(item)}</span>
                  </span>
                </button>
                <button
                  type="button"
                  style={styles.trashButton}
                  aria-label={translate('paymentsUI.web.deleteCard')}
                  onClick={() => props.onRemoveCard(item.id)}
                >
                  <img src={trashIcon} alt="" width={13} height={15} />
                </button>
              </div>
              {selected && (
                <div style={styles.expanded}>
                  <CardDetails
                    cvv={props.cvv}
                    onCvvChange={props.onCvvChange}
                    onCvvError={props.onCvvError}
                    plans={props.installmentPlans}
                    selectedInstallmentId={props.selectedInstallmentId}
                    onSelectInstallment={props.onSelectInstallment}
                    onToggleSplit={props.onToggleSplit}
                    splitMode="add"
                    installmentsLoading={props.installmentsLoading}
                    installmentsErrorMessage={props.installmentsErrorMessage}
                    onRetryInstallments={props.onRetryInstallments}
                  />
                </div>
              )}
            </div>
          </li>
        );
      })}
      {mercadoPago && (
        <WalletRow
          id={mercadoPagoId}
          selected={props.selectedMethodId === mercadoPagoId}
          title={
            props.selectedMethodId === mercadoPagoId
              ? translate('paymentsUI.web.mercadoPagoPayWith')
              : translate('paymentsUI.web.mercadoPagoRow')
          }
          mark={<img src={mercadoPagoMark} alt="" style={styles.brandIcon} />}
          onSelect={props.onSelectMethod}
        >
          {props.selectedMethodId === mercadoPagoId && <MercadoPagoPanel />}
        </WalletRow>
      )}
    </ul>
  );
};

const WalletRow: FC<{
  id: string;
  selected: boolean;
  title: string;
  mark: ReactNode;
  onSelect: (id: string) => void;
  children?: ReactNode;
}> = ({ id, selected, title, mark, onSelect, children }) => (
  <li>
    <div style={{ ...styles.row, ...(selected ? styles.rowSelected : null) }}>
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        style={styles.rowButton}
        onClick={() => onSelect(id)}
      >
        {selected && <span style={{ ...styles.radio, ...styles.radioOn }} />}
        {mark}
        <span style={styles.rowTitle}>{title}</span>
      </button>
      {children && <div style={styles.expanded}>{children}</div>}
    </div>
  </li>
);

const CardDetails: FC<{
  cvv?: string;
  onCvvChange: (cvv: string) => void;
  onCvvError?: (error: string | undefined) => void;
  plans: InstallmentPlan[];
  selectedInstallmentId?: string;
  onSelectInstallment: (id: string) => void;
  onToggleSplit: () => void;
  splitMode: 'add' | 'remove';
  installmentsLoading?: boolean;
  installmentsErrorMessage?: string;
  onRetryInstallments?: () => void;
}> = ({
  cvv,
  onCvvChange,
  onCvvError,
  plans,
  selectedInstallmentId,
  onSelectInstallment,
  onToggleSplit,
  splitMode,
  installmentsLoading,
  installmentsErrorMessage,
  onRetryInstallments,
}) => (
  <>
    <CVVInput value={cvv} onChange={onCvvChange} onError={onCvvError} testID="web-cvv-input" />
    <InstallmentSelector
      plans={plans}
      selectedId={selectedInstallmentId || ''}
      onSelect={onSelectInstallment}
      title={translate('paymentsUI.web.installmentsTitle')}
      testID="web-installments"
      loading={installmentsLoading}
      errorMessage={installmentsErrorMessage}
      onRetry={onRetryInstallments}
    />
    <TogglePaymentCardsButton mode={splitMode} onPress={onToggleSplit} testID="web-toggle-cards" />
  </>
);

const MercadoPagoPanel: FC = () => (
  <div style={styles.mpPanel}>
    <img src={mercadoPagoFlow} alt="" style={styles.mpLogo} />
    <div style={styles.mpFeatured}>
      <img src={mpExternalIcon} alt="" width={24} height={24} style={styles.mpFeaturedIcon} />
      <p style={styles.mpParagraph}>{withBold(translate('paymentsUI.web.mercadoPago.finalize'))}</p>
    </div>
    <ul style={styles.mpList}>
      {MERCADO_PAGO_NOTES.map(({ key, icon }) => (
        <li key={key} style={styles.mpItem}>
          <img src={icon} alt="" width={24} height={24} style={styles.mpItemIcon} />
          <p style={styles.mpParagraph}>{withBold(translate(key))}</p>
        </li>
      ))}
    </ul>
  </div>
);

const CardChoice: FC<{
  item: Extract<PaymentMethodItem, { kind: 'card' }>;
  selected: boolean;
  onSelect: () => void;
  onRemove?: () => void;
  /** Sin caja propia: va dentro de la fila ya seleccionada de la primera tarjeta. */
  plain?: boolean;
}> = ({ item, selected, onSelect, onRemove, plain = false }) => {
  const body = (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        style={{ ...styles.rowButton, flex: 1, width: 'auto' }}
        onClick={onSelect}
      >
        <span style={{ ...styles.radio, ...(selected ? styles.radioOn : null) }} />
        <BrandMark network={item.network} issuer={item.issuerName} />
        <span style={styles.rowTitle}>{rowTitle(item)}</span>
      </button>
      {onRemove && (
        <button
          type="button"
          style={styles.trashButton}
          aria-label={translate('paymentsUI.web.deleteCard')}
          onClick={onRemove}
        >
          <img src={trashIcon} alt="" width={13} height={15} />
        </button>
      )}
    </div>
  );
  if (plain) return body;
  return <div style={{ ...styles.row, ...(selected ? styles.rowSelected : null) }}>{body}</div>;
};

const SameTypeNotice: FC = () => (
  <div style={styles.splitNotice} role="note">
    <span style={styles.splitNoticeIcon} aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="currentColor" />
        <path d="M10 9v5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="10" cy="6.2" r="0.9" fill="#fff" />
      </svg>
    </span>
    <p style={styles.splitNoticeText}>{translate('paymentsUI.splitPayment.sameTypeNotice')}</p>
  </div>
);

const AmountAndCvv: FC<{
  amount?: number;
  onAmountChange: (value: number | undefined) => void;
  cvv?: string;
  onCvvChange: (cvv: string) => void;
  onCvvError?: (error: string | undefined) => void;
  testID: string;
}> = ({ amount, onAmountChange, cvv, onCvvChange, onCvvError, testID }) => (
  <div style={styles.splitFields}>
    <AmountInput
      value={amount}
      onChange={onAmountChange}
      label={translate('paymentsUI.splitPayment.amountLabel')}
      testID={`${testID}-amount`}
    />
    <CVVInput value={cvv} onChange={onCvvChange} onError={onCvvError} testID={`${testID}-cvv`} />
  </div>
);

/** Lista de escritorio para pagar con dos tarjetas (WEB - Flujo Base). */
const DesktopSplit: FC<WebPaymentProps> = (props) => {
  const cards = props.paymentMethods.filter((item) => item.kind === 'card');
  const first = cards.find((item) => item.kind === 'card' && item.id === props.firstCardId) ?? cards[0];
  const others = cards.filter((item) => item.kind === 'card' && item.id !== first?.id);
  const mercadoPago = props.paymentMethods.find((item) => item.kind === 'mercadopago');
  const mercadoPagoId = mercadoPago && mercadoPago.kind === 'mercadopago' ? mercadoPago.id : 'mercadopago';

  const leaveSplit = (id: string) => {
    props.onToggleSplit();
    props.onSelectMethod(id);
  };

  return (
    <ul style={styles.list}>
      {first && first.kind === 'card' && (
        <li>
          <div style={{ ...styles.row, ...styles.rowSelected }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div style={{ ...styles.rowButton, flex: 1, width: 'auto', cursor: 'default' }}>
                <BrandMark network={first.network} issuer={first.issuerName} />
                <span style={styles.rowTitle}>{rowTitle(first)}</span>
              </div>
              <button
                type="button"
                style={styles.trashButton}
                aria-label={translate('paymentsUI.web.deleteCard')}
                onClick={() => props.onRemoveCard(first.id)}
              >
                <img src={trashIcon} alt="" width={13} height={15} />
              </button>
            </div>
            <div style={styles.expanded}>
              <p style={styles.splitLabel}>{translate('paymentsUI.splitPayment.firstCardLabel')}</p>
              <AmountAndCvv
                amount={props.firstAmount}
                onAmountChange={props.onFirstAmountChange}
                cvv={props.cvv}
                onCvvChange={props.onCvvChange}
                onCvvError={props.onCvvError}
                testID="web-split-first"
              />
              <InstallmentSelector
                plans={props.installmentPlans}
                selectedId={props.selectedInstallmentId || ''}
                onSelect={props.onSelectInstallment}
                title={translate('paymentsUI.web.installmentsTitle')}
                testID="web-split-first-installments"
                loading={props.installmentsLoading}
                errorMessage={props.installmentsErrorMessage}
                onRetry={props.onRetryInstallments}
              />
              <TogglePaymentCardsButton mode="remove" onPress={props.onToggleSplit} testID="web-toggle-cards" />
              <SameTypeNotice />
              <p style={styles.splitLabel}>{translate('paymentsUI.splitPayment.secondCardLabel')}</p>
          <div style={styles.list} role="radiogroup" aria-label={translate('paymentsUI.splitPayment.secondCardLabel')}>
            {others.map((item) =>
              item.kind === 'card' ? (
                <CardChoice
                  key={item.id}
                  item={item}
                  selected={props.secondCardId === item.id && !props.addingCard}
                  onSelect={() => {
                    if (props.addingCard) void props.onSaveCard();
                    props.onSelectSecondCard(item);
                  }}
                  onRemove={() => props.onRemoveCard(item.id)}
                />
              ) : null,
            )}
          </div>
              <button
                type="button"
                style={styles.splitAddButton}
                onClick={props.onShowAddCard}
                data-testid="web-split-add-card"
              >
                <img src={addCardIcon} alt="" style={styles.addCardIcon} />
                {translate('paymentsUI.splitPayment.addNewCard')}
              </button>
          {props.addingCard && (
            <AddCardForm
              variant="inline"
              onBack={() => void props.onSaveCard()}
              onSave={props.onSaveCard}
              testID="web-split-add-card-form"
            />
          )}
          {props.secondCardId && !props.addingCard && (
            <>
              <AmountAndCvv
                amount={props.secondAmount}
                onAmountChange={props.onSecondAmountChange}
                cvv={props.secondCvv}
                onCvvChange={props.onSecondCvvChange}
                testID="web-split-second"
              />
              <InstallmentSelector
                plans={props.secondInstallmentPlans}
                selectedId={props.secondInstallmentId || ''}
                onSelect={props.onSelectSecondInstallment}
                title={translate('paymentsUI.web.installmentsTitle')}
                testID="web-split-second-installments"
                loading={props.installmentsLoading}
                errorMessage={props.installmentsErrorMessage}
                onRetry={props.onRetryInstallments}
              />
            </>
          )}
            </div>
          </div>
        </li>
      )}
      {mercadoPago && (
        <WalletRow
          id={mercadoPagoId}
          selected={false}
          title={translate('paymentsUI.web.mercadoPagoRow')}
          mark={<img src={mercadoPagoMark} alt="" style={styles.brandIcon} />}
          onSelect={leaveSplit}
        />
      )}
    </ul>
  );
};

const SplitColumns: FC<WebPaymentProps & { cards: PaymentMethodItem[] }> = ({ cards, ...props }) => (
  <>
    <div style={styles.splitIntro}>
      <div style={styles.splitIntroToggle}>
        <TogglePaymentCardsButton mode="remove" onPress={props.onToggleSplit} testID="web-toggle-cards" />
      </div>
      <SameTypeNotice />
    </div>
    <div style={styles.splitFirstCard}>
    <SplitPaymentCardSection
      contained
      highlightSelection={false}
      onRemoveCard={(item) => item.kind === 'card' && props.onRemoveCard(item.id)}
      cardLabel={translate('paymentsUI.splitPayment.firstCardLabel')}
      cardOptions={cards}
      selectedCardId={props.firstCardId}
      onSelectCard={props.onSelectFirstCard}
      amount={props.firstAmount}
      onAmountChange={props.onFirstAmountChange}
      cvv={props.cvv}
      onCVVChange={props.onCvvChange}
      onCVVError={props.onCvvError}
      installmentPlans={props.installmentPlans}
      selectedInstallmentId={props.selectedInstallmentId}
      onSelectInstallment={props.onSelectInstallment}
      testID="web-split-first-card"
    />
    </div>
    <div style={styles.splitDivider}>
      <SplitSectionDivider />
    </div>
    <div style={styles.block}>
      <SplitPaymentCardSection
        contained
        onAddPress={props.onShowAddCard}
        addLabel={translate('paymentsUI.web.add')}
        onRemoveCard={(item) => item.kind === 'card' && props.onRemoveCard(item.id)}
        cardLabel={translate('paymentsUI.splitPayment.secondCardLabel')}
        cardOptions={cards.filter((item) => item.kind !== 'card' || item.id !== props.firstCardId)}
        selectedCardId={props.secondCardId}
        onSelectCard={props.onSelectSecondCard}
        amount={props.secondAmount}
        onAmountChange={props.onSecondAmountChange}
        cvv={props.secondCvv}
        onCVVChange={props.onSecondCvvChange}
        installmentPlans={props.installmentPlans}
        selectedInstallmentId={props.secondInstallmentId}
        onSelectInstallment={props.onSelectSecondInstallment}
        testID="web-split-second-card"
      />
    </div>
  </>
);
